import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { shopService } from "../services/service";

const EMPTY_PARAMS = {};
const SHOPS_KEY = ["shops"];

export const useShop = (shopId) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [...SHOPS_KEY, "detail", shopId],
    queryFn: () => shopService.getById(shopId),
    enabled: Boolean(shopId),
    staleTime: Infinity,
  });

  const updateShop = useCallback(async (data) => {
    const response = await shopService.update(shopId, data);
    await queryClient.invalidateQueries({ queryKey: SHOPS_KEY });
    return response;
  }, [queryClient, shopId]);

  return {
    shop: query.data?.data?.data ?? query.data?.data ?? null,
    loading: query.isLoading || query.isFetching,
    error: query.error,
    refresh: query.refetch,
    updateShop,
  };
};

export const useShops = (initialParams = EMPTY_PARAMS) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const params = useMemo(() => ({
    page: currentPage,
    per_page: pageSize,
    search: searchTerm,
    status: statusFilter !== "all" ? statusFilter : undefined,
    type: typeFilter !== "all" ? typeFilter : undefined,
    ...initialParams,
  }), [currentPage, pageSize, searchTerm, statusFilter, typeFilter, initialParams]);

  const query = useQuery({
    queryKey: [...SHOPS_KEY, params],
    queryFn: () => shopService.getAll(params),
    staleTime: Infinity,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: SHOPS_KEY }),
    [queryClient]
  );

  const createShop = useCallback(async (data) => {
    const response = await shopService.create(data);
    await invalidate();
    return response;
  }, [invalidate]);

  const registerShop = useCallback(async (data) => {
    const response = await shopService.registerShop(data);
    await invalidate();
    return response;
  }, [invalidate]);

  const updateShop = useCallback(async (id, data) => {
    const response = await shopService.update(id, data);
    await invalidate();
    return response;
  }, [invalidate]);

  const toggleShopStatus = useCallback(async (id) => {
    const response = await shopService.toggleStatus(id);
    await invalidate();
    return response;
  }, [invalidate]);

  return {
    shops: query.data?.data?.data || [],
    pagination: query.data?.data || null,
    loading: query.isLoading || query.isFetching,
    error: query.error,
    searchTerm, setSearchTerm,
    statusFilter, setStatusFilter,
    typeFilter, setTypeFilter,
    currentPage, setCurrentPage,
    pageSize, setPageSize,
    fetchShops: query.refetch,
    refresh: query.refetch,
    createShop,
    registerShop,
    updateShop,
    toggleShopStatus,
    getShopTypes: () => shopService.getTypes(),
    getShopStats: () => shopService.getStats(),
  };
};